/**
 * ForgePanel Component
 *
 * Main sliding panel container for FORGE
 * Renders header, mode switcher, and mode-specific content
 */

import React, { useState } from 'react';
import { useForge } from '../hooks/useForge';
import { ModeSwitch } from './ModeSwitch';
import { QuickModeChat } from './QuickModeChat';
import { DetailedModeForm } from './DetailedModeForm';
import { StepIndicator } from './StepIndicator';
import { ExecutionProgress } from './ExecutionProgress';
import { RequirementPreview } from './RequirementPreview';
import type { ForgePanelProps, ForgeMode, DetailedModeStep } from '../types/forge';
import styles from '../styles/forge.module.css';

export const ForgePanel: React.FC<ForgePanelProps> = ({
  isOpen,
  onClose,
  defaultMode = 'quick',
  serviceId,
  pageContext,
}) => {
  const [mode, setMode] = useState<ForgeMode>(defaultMode);

  const forge = useForge();

  // Derive detailed step from draft state in context
  const detailedStep: DetailedModeStep = forge.detailedDraft?.step || 'type';

  // Map step to index for StepIndicator
  const getStepIndex = (step: DetailedModeStep): number => {
    const stepMap = { type: 0, details: 1, review: 2 };
    return stepMap[step];
  };

  const handleModeChange = (newMode: ForgeMode) => {
    setMode(newMode);
  };

  const handleClose = () => {
    // Just close the panel — draft state persists in context
    onClose();
  };

  const handleQuickSubmit = async (message: string) => {
    await forge.submitQuick(message);
  };

  const handleDetailedSubmit = async (data: any) => {
    await forge.submitDetailed(data);
  };

  const handleExecutionComplete = (prUrl: string) => {
    console.log('Execution completed:', prUrl);
  };

  const handleExecutionError = (error: string) => {
    console.error('Execution failed:', error);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className={styles.forgePanelOverlay} onClick={handleClose} />
      <div className={styles.forgePanel}>
        <div className={styles.forgePanelHeader}>
          <div className={styles.forgePanelHeaderLeft}>
            <div className={styles.forgePanelTitle}>
              <h2>FORGE</h2>
              <p>Feature-Oriented Rapid Generation Engine</p>
            </div>
            {serviceId && (
              <span className={styles.forgePanelBadge}>{serviceId}</span>
            )}
          </div>
          <button
            onClick={handleClose}
            className={styles.forgePanelClose}
            aria-label="Close panel"
          >
            ×
          </button>
        </div>

        <div className={styles.forgePanelModeSwitch}>
          <ModeSwitch
            currentMode={mode}
            onChange={handleModeChange}
            disabled={forge.isSubmitting || forge.isExecuting}
          />
        </div>

        {/* Step Indicator for Detailed Mode */}
        {mode === 'detailed' && !forge.isExecuting && !forge.requirement && (
          <StepIndicator
            currentStep={getStepIndex(detailedStep)}
            steps={['Type', 'Details', 'Review']}
          />
        )}

        <div className={styles.forgePanelContent}>
          {forge.isExecuting && forge.executionStatus ? (
            <ExecutionProgress
              requirementId={forge.executionStatus.requirementId}
              onComplete={handleExecutionComplete}
              onError={handleExecutionError}
            />
          ) : forge.requirement &&
            forge.requirement.status !== 'draft' &&
            !forge.isSubmitting ? (
            <RequirementPreview
              requirement={forge.requirement}
              onExecute={() => forge.triggerExecution(forge.requirement!.id)}
            />
          ) : mode === 'quick' ? (
            <QuickModeChat
              onSubmit={handleQuickSubmit}
              isSubmitting={forge.isSubmitting}
              requirement={forge.requirement || undefined}
              error={forge.error || undefined}
            />
          ) : (
            <DetailedModeForm
              onSubmit={handleDetailedSubmit}
              isSubmitting={forge.isSubmitting}
              requirement={forge.requirement || undefined}
              error={forge.error || undefined}
              serviceId={serviceId}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ForgePanel;
